import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subscriber, map } from 'rxjs';
import { ApiService } from '../../../../core/services/ApiService';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { environment } from '../../../../../environments/environment';
import { IdentityStore } from '../../../auth/core/stores/IdentityStore';
import { TerminalsStore } from '../../../terminals/core/stores/TerminalsStore';
import { LanguageStore } from '../../../../core/stores/LanguageStore';
import { IOtsApiOrder } from '../interfaces/IOtsApiOrder';
import { IOtsStaffOrder } from '../interfaces/IOtsStaffOrder';
import { OtsApiOrderStatus } from '../types';
import { OtsStaffOrderUtils } from '../utils/OtsStaffOrderUtils';

@Injectable({ providedIn: 'root' })
export class OtsStaffKitchenService {

  private readonly _apiService = inject(ApiService);
  private readonly _identityStore = inject(IdentityStore);
  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _languageStore = inject(LanguageStore);

  public watchOrders(cinemaId: number, onAttemptFailed: (message: string) => void): Observable<IOtsStaffOrder[]> {
    return new Observable(subscriber => {
      const abort = new AbortController();
      let stopped = false;
      void this.keepOrderStream(cinemaId, abort.signal, subscriber, () => stopped, onAttemptFailed);
      return () => {
        stopped = true;
        abort.abort();
      };
    });
  }

  public moveKitchenOrderToNextStatus(
    orderId: string,
    cinemaId: number,
    expectedStatus: OtsApiOrderStatus
  ): Observable<IOtsStaffOrder> {
    return this._apiService.post(
      `/kitchen/orders/${encodeURIComponent(orderId)}/move-to-next-status`,
      {},
      new HttpParams().set('cinemaId', String(cinemaId)).set('expectedStatus', expectedStatus)
    ).pipe(
      map((response: IResponse<IOtsApiOrder>) => {
        if (!this.envelopeOk(response) || !response.data) {
          throw new Error(response?.message || 'OtsRequestFailed');
        }

        const lines = (response.data.lines ?? []).map(line => OtsStaffOrderUtils.cartLineFromApi(line));
        return OtsStaffOrderUtils.fromApi(response.data, lines);
      })
    );
  }

  private async keepOrderStream(
    cinemaId: number,
    signal: AbortSignal,
    subscriber: Subscriber<IOtsStaffOrder[]>,
    isStopped: () => boolean,
    onAttemptFailed: (message: string) => void
  ): Promise<void> {
    let delayMs = 1000;
    while (!isStopped()) {
      try {
        const response = await fetch(this.streamUrl(cinemaId), {
          headers: this.streamHeaders(),
          signal
        });

        if (response.status === 401 || response.status === 403) {
          subscriber.error(new Error('GuestOrderingStaffForbidden'));
          return;
        }

        if (!response.ok || !response.body) {
          throw new Error('OtsRequestFailed');
        }

        delayMs = 1000;
        await this.readOrderStream(response.body, subscriber, signal);
      } catch (error) {
        if (isStopped() || signal.aborted) {
          return;
        }

        if (error instanceof Error && error.message === 'GuestOrderingStaffForbidden') {
          subscriber.error(error);
          return;
        }

        onAttemptFailed(error instanceof Error ? error.message : 'OtsRequestFailed');
      }

      if (isStopped() || signal.aborted) {
        return;
      }

      await this.wait(delayMs, signal);
      delayMs = Math.min(delayMs * 2, 15000);
    }
  }

  private streamUrl(cinemaId: number): string {
    return `${environment.apiUrl}/kitchen/orders/stream?cinemaId=${encodeURIComponent(String(cinemaId))}`;
  }

  private streamHeaders(): Headers {
    const headers = new Headers({
      Accept: 'text/event-stream',
      'X-app': String(environment.app),
      'x-version-prefix': environment.versionPrefix,
      'x-gui-version': environment.version
    });
    const identity = this._identityStore.identity() ?? this._identityStore.tmpIdentity();
    if (identity?.accessToken) {
      headers.set('Authorization', `Bearer ${identity.accessToken}`);
    }

    const terminal = this._terminalsStore.selectedTerminal();
    if (terminal) {
      headers.set('TerminalId', String(terminal.id));
      headers.set('CinemaId', String(terminal.cinemaId));
      headers.set('SessionId', terminal.currentSession?.id != null ? String(terminal.currentSession.id) : '');
      headers.set('WorkerNumber', terminal.worker?.number ?? '');
      headers.set('MandatorId', String(terminal.mandator.id));
      headers.set('BasketId', terminal.currentSession?.currentBasketId != null
        ? String(terminal.currentSession.currentBasketId)
        : '');
    }

    const language = this._languageStore.language()?.key;
    if (language) {
      headers.set('X-Lang', language);
    }

    return headers;
  }

  private async readOrderStream(
    body: ReadableStream<Uint8Array>,
    subscriber: Subscriber<IOtsStaffOrder[]>,
    signal: AbortSignal
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let orders: IOtsStaffOrder[] = [];

    while (!signal.aborted) {
      const chunk = await reader.read();
      if (chunk.done) {
        return;
      }

      buffer += decoder.decode(chunk.value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        const parsed = this.parseSseFrame(frame);
        if (!parsed || parsed.event === 'ping' || !parsed.data) {
          continue;
        }

        if (parsed.event === 'snapshot') {
          orders = this.mapStreamOrders(JSON.parse(parsed.data) as IOtsApiOrder[]);
          subscriber.next(orders);
        }

        if (parsed.event === 'upsert') {
          const order = this.mapStreamOrder(JSON.parse(parsed.data) as IOtsApiOrder);
          orders = orders.some(item => item.id === order.id)
            ? orders.map(item => item.id === order.id ? order : item)
            : [...orders, order];
          subscriber.next([...orders]);
        }
      }
    }
  }

  private parseSseFrame(frame: string): { event: string; data: string } | null {
    let eventName = 'message';
    const data: string[] = [];

    for (const line of frame.split('\n')) {
      if (line.startsWith(':') || line.length === 0) {
        continue;
      }

      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      }

      if (line.startsWith('data:')) {
        data.push(line.slice('data:'.length).trim());
      }
    }

    if (data.length === 0 && eventName === 'message') {
      return null;
    }

    return { event: eventName, data: data.join('\n') };
  }

  private mapStreamOrders(orders: IOtsApiOrder[]): IOtsStaffOrder[] {
    return (orders ?? []).map(order => this.mapStreamOrder(order));
  }

  private mapStreamOrder(order: IOtsApiOrder): IOtsStaffOrder {
    const lines = (order.lines ?? []).map(line => OtsStaffOrderUtils.cartLineFromApi(line));
    return OtsStaffOrderUtils.fromApi(order, lines);
  }

  private wait(delayMs: number, signal: AbortSignal): Promise<void> {
    return new Promise(resolve => {
      const timer = window.setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, delayMs);
      const onAbort = () => {
        window.clearTimeout(timer);
        resolve();
      };
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  private envelopeOk(response: { success?: boolean; isSuccess?: boolean } | null | undefined): boolean {
    return response?.success === true || response?.isSuccess === true;
  }
}
